import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { Driver, trpc } from 'src/trpc';
import { DriverStatus } from '@libs/schemas/src/driver';
import { DriverZ, driverStatuses, driverZ } from '@libs/schemas';

import { useToast } from 'src/components/useToast';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { PhoneInput } from 'components/ui/PhoneInput';
import { Loader } from 'components/Loader';

type FormState = {
  isValid: boolean;
};

interface Props extends PropsWithChildren {
  driver?: Driver;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onStateChange?: (state: FormState) => void;
}

export const DriverDetailsForm: FC<Props> = ({
  driver,
  onSuccess,
  onError,
  onStateChange,
  children,
}) => {
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const { errorToast } = useToast();

  const utils = trpc.useUtils();
  const { mutate, isPending } = trpc.admin.driver.updateDriver.useMutation({
    onSuccess: () => {
      onSuccess?.();
      utils.admin.driver.getAllDrivers.invalidate();
      setEmailError(null);
      setPhoneError(null);
    },
    onError: (error) => {
      // Check for specific duplicate key errors
      if (error.message.includes('idx_drivers_email')) {
        setEmailError('This email is already in use by another driver');
        onError?.(new Error('This email is already in use by another driver'));
      } else if (error.message.includes('idx_drivers_phone')) {
        setPhoneError('This phone number is already in use by another driver');
        onError?.(new Error('This phone number is already in use by another driver'));
      } else {
        onError?.(new Error(error.message || 'An unknown error occurred'));
      }
    },
  });

  const form = useForm({
    resolver: zodResolver(driverZ),
    defaultValues: driver || {
      name: { first: '', last: '' },
      email: '',
      phone: '',
      status: 'available',
    },
  });

  const { isValid } = useFormState({ control: form.control });

  useEffect(() => {
    onStateChange?.({ isValid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  // Reset field-specific errors when values change
  useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (name === 'email') {
        setEmailError(null);
      } else if (name === 'phone') {
        setPhoneError(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = (values: DriverZ) => {
    setEmailError(null);
    setPhoneError(null);

    if (!driver?._id) {
      const msg = 'Driver ID is required to update a driver';
      onError?.(new Error(msg));
      errorToast({ title: 'Error', description: msg });
      return;
    }

    mutate({
      ...values,
      _id: driver._id,
    });
  };

  if (isPending) {
    return <Loader title="Updating driver details" />;
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="name.first"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label mb-1">First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name.last"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Email</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email" {...field} />
                </FormControl>
                {emailError && <p className="text-sm font-medium text-destructive">{emailError}</p>}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Phone Number</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                {phoneError && <p className="text-sm font-medium text-destructive">{phoneError}</p>}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Status</FormLabel>
                <FormControl>
                  <Select
                    {...field}
                    onValueChange={(value) => {
                      field.onChange(value as DriverStatus);
                    }}
                  >
                    <SelectTrigger className="text-text-light">
                      <div className="flex flex-row gap-2">
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {driverStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {children}
        </div>
      </form>
    </Form>
  );
};
